import {
  WSActions,
  WSAlert,
  WSCard,
  WSControl,
  WSEmptyState,
  WSInfoBox,
  WSList,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useQueryMemberPrivateFile } from "../../../../query/files/queries";
import { useMutationCreateInsuranceImport } from "../../../../query/insuranceImport/mutations/useMutationCreateInsuranceImport";
import { useMutationUpdateInsuranceImport } from "../../../../query/insuranceImport/mutations/useMutationUpdateInsuranceImport";
import { useQueryInsuranceImport } from "../../../../query/insuranceImport/queries/useQueryInsuranceImport";
import { WSQueries } from "../../../../query/WSQuery";
import type { insuranceImportApiSpec } from "@wingspanhq/wingspan-api-types";
import { StepComponent } from "../../../../shared/components/ModalFlow/types";
import { ValidationProgress } from "../../modules/SharedFile/components/ValidationProgress";
import { RequirementContextType } from "../../types";
import { Policies } from "./Policies";
import { FlowUploadDocumentProps } from "./types";
import { useModalUploadFile } from "./useModalUploadFile";
import { getPolicies } from "./utils";
import { ValidationItem } from "./ValidationItem";

const NoFile: React.FC<{ onUpload: () => void }> = ({ onUpload }) => {
  return (
    <WSList gap="2XL">
      <WSCard header={{ label: "Upload document" }}>
        <WSEmptyState
          theme="neutral"
          type="documents"
          actions={{
            buttons: [
              {
                label: "Upload document",
                onClick: onUpload
              }
            ]
          }}
          title="Upload your insurance document"
          description="Upload a document to import and process as insurance policies."
          orientation="horizontal"
        />
      </WSCard>
    </WSList>
  );
};

const Uploading: React.FC<{
  fileName: string;
}> = ({ fileName }) => {
  return (
    <WSList gap="L">
      <WSCard
        header={{
          label: fileName || "Document",
          value: {
            pill: {
              text: "Uploading",
              theme: "neutral"
            }
          }
        }}
      >
        <WSInfoBox
          theme="neutral"
          icon="loader"
          transparent
          iconColor="blue400"
        >
          A document is uploading. Please, do not close this page.
        </WSInfoBox>
      </WSCard>
    </WSList>
  );
};

const ErrorUploading: React.FC<{ fileName: string }> = ({ fileName }) => {
  return (
    <WSList gap="L">
      <WSAlert theme="error" title="Upload failed" icon="alert-circle">
        Something went wrong with the upload. Please try the upload again or
        upload a new file. If the issue persists, please contact support.
      </WSAlert>
      <WSCard
        header={{
          label: fileName,
          value: {
            pill: { text: "Upload failed", theme: "error" }
          }
        }}
      >
        <WSProgressBar
          my="L"
          percent={100}
          theme="error"
          text={
            <WSInfoBox mt="S" theme="warning" icon="alert-circle" transparent>
              There was an error uploading the file
            </WSInfoBox>
          }
        />
      </WSCard>
    </WSList>
  );
};

const Processing: React.FC<{
  fileName: string;
  jobs: insuranceImportApiSpec.InsuranceImportJob[];
}> = ({ fileName, jobs }) => {
  const validationJobs = jobs?.filter(j => j.type === "Validation") || [];

  return (
    <WSList gap="L">
      <WSCard
        header={{
          label: fileName,
          value: {
            pill: {
              text: "Validating",
              theme: "neutral"
            }
          }
        }}
      >
        <ValidationProgress
          jobs={validationJobs}
          isBackgroundValidation={false}
        />

        <WSInfoBox mt="L">
          Do not close or exit the window until the validation is finalized or
          you will have to start the process over.
        </WSInfoBox>
      </WSCard>

      {getValidations(jobs)}
    </WSList>
  );
};

const Importing: React.FC<{
  fileName: string;
  jobs: insuranceImportApiSpec.InsuranceImportJob[];
}> = ({ fileName, jobs }) => {
  const extractionJob = jobs?.find(j => j.type === "Extraction")!;
  const policies = getPolicies(extractionJob);

  return (
    <WSList gap="L">
      <WSCard
        header={{
          label: fileName,
          value: {
            pill: {
              text: "Awaiting import",
              theme: "warning"
            }
          }
        }}
      >
        <WSList gap="L">
          <WSInfoBox transparent icon="file" color="gray500" mt="S">
            A document is uploaded and ready to be imported and processed as
            insurance policies.
          </WSInfoBox>

          <WSAlert theme="info" title="Pro tip" icon="bulb">
            Review the extracted data and policies below to check that all
            information is accurate prior to completing the import process. If
            there are any inaccuracies or the wrong file was uploaded, upload a
            new file a try again.
          </WSAlert>
        </WSList>
      </WSCard>

      <Policies policies={policies} />
    </WSList>
  );
};

const InvalidDocument: React.FC<{
  fileName: string;
  jobs: insuranceImportApiSpec.InsuranceImportJob[];
}> = ({ fileName, jobs }) => {
  return (
    <WSList gap="L">
      <WSAlert theme="error" title="Invalid document" icon="alert-circle">
        The document that was uploaded is invalid or illegible and could not be
        processed, please check the document and try again or upload a new file.
      </WSAlert>

      <WSCard
        header={{
          label: fileName,
          value: {
            pill: {
              icon: "alert-circle",
              text: "Invalid document",
              theme: "error"
            }
          }
        }}
      >
        <WSProgressBar my="L" percent={100} theme="error" />

        <WSInfoBox theme="neutral" icon="time-five" transparent>
          Invalid document uploaded
        </WSInfoBox>
      </WSCard>

      {getValidations(jobs)}
    </WSList>
  );
};

const getValidations = (jobs: insuranceImportApiSpec.InsuranceImportJob[]) => {
  const validationsJobs = jobs.filter(job => job.type === "Validation");

  return (
    <>
      {validationsJobs.map((validation, index) => (
        <ValidationItem
          key={`${validation.title}-${index}`}
          item={validation}
          contextType={RequirementContextType.Payer}
        />
      ))}
    </>
  );
};

export const Step2: StepComponent<FlowUploadDocumentProps> = ({
  state,
  setState,
  onNext,
  goToStep,
  goToSideStep,
  onBack,
  children,
  onClose
}) => {
  const { fileId, insuranceImportId } = state;

  const [certified, setCertified] = useState(false);
  const createIdempotencyKey = useRef(uuidv4());
  const lastAutoCreateFileId = useRef<string>();

  const uploadDocument = useModalUploadFile();

  const queryPrivateFile = useQueryMemberPrivateFile(fileId!, {
    enabled: !!fileId
  });

  const [createInsuranceImport, createInsuranceImportMeta] =
    useMutationCreateInsuranceImport();
  const [updateInsuranceImport, updateInsuranceImportMeta] =
    useMutationUpdateInsuranceImport();

  const queryInsuranceImport = useQueryInsuranceImport(insuranceImportId!, {
    enabled: !!insuranceImportId && !!fileId,
    refetchInterval: result => {
      return result?.status === "Processing" ? 3000 : false;
    }
  });

  const onCreateInsuranceImport = useCallback(() => {
    createInsuranceImport(createIdempotencyKey.current, {
      onSuccess: response => {
        setState({
          insuranceImportId: response.insuranceImportId
        });
      }
    });
  }, [createInsuranceImport, setState]);

  const onUploadNewFile = useCallback(() => {
    uploadDocument.open({
      onSuccess(file) {
        createInsuranceImportMeta.reset();
        updateInsuranceImportMeta.reset();

        queryInsuranceImport.remove();
        queryPrivateFile.remove();
        setState({
          fileId: file.fileId
        });
      }
    });
  }, [updateInsuranceImportMeta.reset]);

  useEffect(() => {
    if (
      !insuranceImportId &&
      fileId &&
      lastAutoCreateFileId.current !== fileId
    ) {
      lastAutoCreateFileId.current = fileId;
      onCreateInsuranceImport();
    }
  }, [insuranceImportId, fileId, onCreateInsuranceImport]);

  useEffect(() => {
    if (!fileId) {
      onUploadNewFile();
    }
  }, [fileId]);

  useEffect(() => {
    if (
      fileId &&
      insuranceImportId &&
      queryInsuranceImport.isSuccess &&
      queryInsuranceImport.data?.fileId !== fileId
    ) {
      updateInsuranceImport({
        insuranceImportId,
        request: {
          fileId
        }
      });
    }
  }, [
    fileId,
    insuranceImportId,
    queryInsuranceImport.isSuccess,
    queryInsuranceImport.data?.fileId
  ]);

  if (!fileId) {
    return <NoFile onUpload={onUploadNewFile} />;
  }

  const queryError = queryPrivateFile.error || queryInsuranceImport.error;

  const onRetryQuery = () => {
    if (queryPrivateFile.error) {
      void queryPrivateFile.refetch();
    }
    if (queryInsuranceImport.error) {
      void queryInsuranceImport.refetch();
    }
  };

  if (createInsuranceImportMeta.error && !insuranceImportId) {
    return (
      <WSList gap="2XL">
        <ErrorUploading
          fileName={queryPrivateFile.data?.filename || "Document"}
        />
        <WSErrorMessage
          contextKey="Other"
          error={createInsuranceImportMeta.error}
        />
        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Cancel",
              onClick: onClose,
              kind: "Secondary"
            },
            {
              label: "Upload new file",
              onClick: onUploadNewFile,
              kind: "Secondary"
            },
            {
              label: "Retry",
              onClick: onCreateInsuranceImport,
              kind: "Primary"
            }
          ]}
        />
      </WSList>
    );
  }

  if (!insuranceImportId && createInsuranceImportMeta.isLoading) {
    return (
      <Uploading fileName={queryPrivateFile.data?.filename || "Document"} />
    );
  }

  if (queryError) {
    return (
      <WSList gap="2XL">
        <ErrorUploading
          fileName={queryPrivateFile.data?.filename || "Document"}
        />
        <WSErrorMessage contextKey="Other" error={queryError} />
        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Cancel",
              onClick: onClose,
              kind: "Secondary"
            },
            {
              label: "Upload new file",
              onClick: onUploadNewFile,
              kind: "Secondary"
            },
            {
              label: "Retry",
              onClick: onRetryQuery,
              kind: "Primary"
            }
          ]}
        />
      </WSList>
    );
  }

  return (
    <WSQueries queries={{ queryPrivateFile, queryInsuranceImport }}>
      {({ queryPrivateFileData, queryInsuranceImportData }) => {
        const isProcessing = queryInsuranceImportData.status === "Processing";

        const isReadyToSubmit =
          queryInsuranceImportData.status === "PendingSubmission";

        const isInvalidDocument =
          queryInsuranceImportData.status === "Rejected";

        const isError =
          !!createInsuranceImportMeta.error ||
          !!updateInsuranceImportMeta.error;

        const isUploading =
          queryPrivateFile.isLoading ||
          updateInsuranceImportMeta.isLoading ||
          createInsuranceImportMeta.isLoading ||
          queryInsuranceImportData.fileId !== fileId;

        const extractionJob =
          queryInsuranceImport.data?.jobs.find(
            job => job.type === "Extraction"
          ) || undefined;
        const validPolicies = extractionJob
          ? getPolicies(extractionJob, true)
          : [];

        return (
          <WSList gap="2XL">
            {isError ? (
              <ErrorUploading fileName={queryPrivateFileData.filename} />
            ) : isUploading ? (
              <Uploading fileName={queryPrivateFileData.filename} />
            ) : isInvalidDocument ? (
              <InvalidDocument
                jobs={queryInsuranceImportData.jobs}
                fileName={queryPrivateFileData.filename}
              />
            ) : isProcessing ? (
              <Processing
                jobs={queryInsuranceImportData.jobs}
                fileName={queryPrivateFileData.filename}
              />
            ) : isReadyToSubmit ? (
              <>
                <Importing
                  fileName={queryPrivateFileData.filename}
                  jobs={queryInsuranceImportData.jobs}
                />
                <WSCard>
                  <WSList gap="M">
                    <WSControl
                      value={certified}
                      type="checkbox"
                      onChange={setCertified}
                      size="S"
                      label={
                        <WSText.ParagraphSm>
                          I certify that the Certificate of Insurance I am
                          uploading is a true, correct, and unaltered copy
                          issued by the insurer/producer; that the information
                          on it is accurate as of the date of submission; and
                          that I have reviewed and confirm the extracted fields
                          in this form match the certificate.
                        </WSText.ParagraphSm>
                      }
                    />
                    <WSInfoBox>
                      The import action can't be rolled back, it will import the
                      policies as you see them here. Should the policies expire
                      or change in the future, you can upload new documents to
                      keep your profile up to date.
                    </WSInfoBox>
                  </WSList>
                </WSCard>
              </>
            ) : null}

            <WSErrorMessage
              contextKey="Other"
              error={
                createInsuranceImportMeta.error ||
                updateInsuranceImportMeta.error
              }
            />

            <WSActions
              alignment="fill"
              buttons={[
                {
                  label: "Cancel",
                  visible: !isReadyToSubmit,
                  onClick: onClose,
                  kind: "Secondary"
                },
                {
                  label: "Retry",
                  visible: !!updateInsuranceImportMeta.error,
                  onClick: () => {
                    if (!insuranceImportId) {
                      return;
                    }
                    updateInsuranceImportMeta.reset();
                    updateInsuranceImport({
                      insuranceImportId,
                      request: { fileId }
                    });
                  },
                  kind: "Primary"
                },
                {
                  label: "Upload new file",
                  disabled: !insuranceImportId || isProcessing,
                  onAsyncClick: async () => {
                    onUploadNewFile();
                  },
                  kind:
                    isReadyToSubmit || !!updateInsuranceImportMeta.error
                      ? "Secondary"
                      : "Primary"
                },
                {
                  label: "Import",
                  disabled:
                    !isReadyToSubmit ||
                    !certified ||
                    validPolicies.length === 0,
                  visible: isReadyToSubmit,
                  kind: "Primary",
                  onClick: onNext
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSQueries>
  );
};
